import React, { useState, useMemo, useCallback, useRef } from 'react'
import Immutable from 'immutable'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'
import { debounce } from 'lodash'
import {
  MANAGEMENT_UI_TEAMS,
  MANAGEMENT_UI_EVERYTHING,
  MANAGEMENT_UI_SCHEDULING,
} from 'constants/index'
import withPromise from 'helpers/withPromise'
import {
  setSelectedOtherFilters,
  fetchManagementUIData,
} from 'reducers/managementUI/reducer'
import { TablePagination, Tab, Tabs } from '@material-ui/core'
import { ReactComponent as NoJobs } from 'assets/img/management-ui-no-jobs.svg'
import { saveManagementUIFilters } from 'reducers/profile/reducer'
import CircleLoader from 'components/Loading/CircleLoader'
import { STATUS_COMPLETED, STATUS_IN_PROGRESS } from 'helpers/jobs'
import 'styles/app/apps/management-ui.scss'
import { DELETION_REASON_FIELDS } from 'routes/App/JobsAdmin/Jobs/JobWorkflowForm/constants'
import {
  FILTER_TYPE_ASSIGNED_USER,
  FILTER_TYPE_ASSIGNED_USER_GROUP,
  FILTER_TYPE_CREATED_BY,
  FILTER_TYPE_OVERDUE,
  FILTER_TYPE_RECURRENCE,
  FILTER_TYPE_STATUS,
  FILTER_TYPE_MEDIA,
  FILTER_TYPE_DUE_DATE,
} from 'constants/managementUI'
import ConfirmDialog from 'components/common/Popup/ConfirmDialog'
import { REACT_INTL_PROP_TYPE } from 'constants/propTypes'
import DeletionReason from 'routes/App/Apps/MyJobs/Common/DeletionReasonField'
import {
  ManagementUITitle,
  ManagementUIAppWrapper,
  PaginationContainer,
  JobListWrapper,
  StatusIcon,
  StyledTabs,
  ManagementUITitleContainer,
} from './ManagementUI.styles'
import messages from './translate'
import JobsList from './common/JobsList/JobsList'
import FilterBar from './FilterBar/FilterBar'
import NoDataScreen from './common/NoDataScreen'
import PlanNewJob from './PlanNewJob/PlanNewJob'
import { Colors } from './ManagementUI.types'

function ManagementUI({
  intl: { formatMessage },
  isLoadingData,
  isUpdatingData,
  jobsData,
  jobsManagementFilters,
  saveManagementUIFilters,
  setSelectedOtherFilters,
  featureFlags,
  fetchManagementUIData,
  storePsfs,
  jobsCount,
}) {
  const [isSidebarOpened, setIsSidebarOpened] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const data = jobsData.toJS()
  const {
    [FILTER_TYPE_OVERDUE]: appliedOverdue,
    [FILTER_TYPE_ASSIGNED_USER]: appliedUsers,
    [FILTER_TYPE_ASSIGNED_USER_GROUP]: appliedUserGroups,
    [FILTER_TYPE_CREATED_BY]: appliedOtherCreatedBy,
    [FILTER_TYPE_RECURRENCE]: appliedRecurrence,
    [FILTER_TYPE_STATUS]: appliedStatus,
    [FILTER_TYPE_MEDIA]: appliedMedia,
    [FILTER_TYPE_DUE_DATE]: appliedDueDate,
    tab,
  } = jobsManagementFilters

  const scrollRef = useRef(null)
  const [sortColumn, setSortColumn] = useState('')
  const [sortDirection, setSortDirection] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDialogOpened, setIsDialogOpened] = useState(false)
  const [confirmDialogSettings, setConfirmDialogSettings] = useState({
    popupAction: () => () => {},
    popupTitle: '',
    needsDeletionReason: false,
    confirmText: formatMessage(messages.delete),
    // eslint-disable-next-line react/jsx-no-useless-fragment
    popupMessage: <></>,
  })
  const [jobContextMenuPosition, setJobContextMenuPosition] = useState({
    top: 0,
    left: 0,
  })
  const [tmpSites, setTmpSites] = useState([])
  const [tmpUsers, setTmpUsers] = useState([])
  const [tmpUserGroups, setTmpUserGroups] = useState([])
  const [tmpCreatedBy, setTmpCreatedBy] = useState([])
  const [tmpRecurrence, setTmpRecurrence] = useState([])
  const [tmpStatus, setTmpStatus] = useState([])
  const [deletionReasonFieldValue, setDeletionReasonFieldValue] = useState(null)

  const toggleIsSidebarOpened = () => setIsSidebarOpened(!isSidebarOpened)

  const activeTab = useMemo(
    () =>
      tab === MANAGEMENT_UI_SCHEDULING && !featureFlags.JOB_SCHEDULING_V2
        ? MANAGEMENT_UI_EVERYTHING
        : tab,
    [tab, featureFlags.JOB_SCHEDULING_V2],
  )

  const actualTab = useMemo(
    () =>
      !activeTab || activeTab === MANAGEMENT_UI_TEAMS
        ? MANAGEMENT_UI_EVERYTHING
        : activeTab,
    [activeTab],
  )

  const isSchedulingTab = useMemo(
    () =>
      activeTab === MANAGEMENT_UI_SCHEDULING && featureFlags.JOB_SCHEDULING_V2,
    [activeTab, featureFlags.JOB_SCHEDULING_V2],
  )

  const fetchData = (tabName, filtersQuery, search, pagination, sort) => {
    withPromise(fetchManagementUIData, {
      tabName,
      query: filtersQuery,
      search,
      pagination,
      sort,
    }).then(() => {
      setIsUpdating(false)
    })
  }

  const fetchDataDebounced = useCallback(debounce(fetchData, 1000), [])

  const fetchManagementUITable = () => {
    /* eslint no-underscore-dangle: 0 */
    const filtersQuery = storePsfs._filters?.getFiltersQuery()
    /* eslint no-underscore-dangle: 0 */
    const pagination = storePsfs._pagination?.getPagination()
    /* eslint no-underscore-dangle: 0 */
    const sort = storePsfs._sort?.getSort()

    setIsUpdating(true)
    if (filtersQuery) {
      fetchDataDebounced(activeTab, filtersQuery, searchValue, pagination, sort)
    } else {
      setIsUpdating(false)
    }
  }

  const onClearFilter = () => {
    const saveFiltersPayload = {
      jobs_management_filters: {
        [FILTER_TYPE_CREATED_BY]: [],
        [FILTER_TYPE_ASSIGNED_USER]: [],
        [FILTER_TYPE_ASSIGNED_USER_GROUP]: [],
        [FILTER_TYPE_RECURRENCE]: [],
        [FILTER_TYPE_STATUS]: [],
        [FILTER_TYPE_MEDIA]: null,
        [FILTER_TYPE_DUE_DATE]: null,
      },
    }

    setIsUpdating(true)
    setTmpSites(tmpSites)
    setTmpUsers([])
    setTmpUserGroups([])
    setTmpCreatedBy([])
    setTmpRecurrence([])
    setTmpStatus([])
    setSelectedOtherFilters([])

    saveManagementUIFilters(saveFiltersPayload)
  }

  const onCloseDeleteJobDialog = () => {
    setIsDialogOpened(false)
  }

  const renderNoDataScreen = () => {
    const onlyDateAndSiteApplied =
      appliedOverdue === null &&
      appliedDueDate === null &&
      appliedMedia === null &&
      !appliedRecurrence.length &&
      !appliedStatus.length &&
      !appliedOtherCreatedBy.length &&
      !appliedUsers.length &&
      !appliedUserGroups.length

    return (
      <NoDataScreen
        onClearFilter={onClearFilter}
        isUpdating={isUpdating}
        specialNoResultScreen={
          onlyDateAndSiteApplied ? (
            <>
              <NoJobs className="no-jobs-image" />
              <div className="no-jobs-title">
                {formatMessage(messages.noActiveJobsMessage)}
              </div>
            </>
          ) : null
        }
      />
    )
  }

  const changeSortColumn = column => {
    const order = column === sortColumn ? !sortDirection : false

    setSortColumn(column)
    setSortDirection(order)
  }

  const getStatusIcon = iconState => {
    let color = Colors.blue

    switch (iconState) {
      case STATUS_COMPLETED:
        color = Colors.green
        break
      case STATUS_IN_PROGRESS:
        color = Colors.yellow
        break
      default:
        break
    }

    return <StatusIcon color={color} />
  }

  const isLoading = useMemo(
    () => isLoadingData || isUpdatingData || isUpdating,
    [isLoadingData, isUpdatingData, isUpdating],
  )

  const scrollToTop = () => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const renderResults = () =>
    data.length ? (
      <JobListWrapper ref={scrollRef}>
        <JobsList
          jobs={data}
          getStatusIcon={getStatusIcon}
          activeTab={actualTab}
          setIsDialogOpened={setIsDialogOpened}
          setConfirmDialogSettings={setConfirmDialogSettings}
          setJobContextMenuPosition={setJobContextMenuPosition}
          changeSortColumn={changeSortColumn}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          isSchedulingTab={isSchedulingTab}
          fetchManagementUITable={fetchManagementUITable}
          isLoading={isLoading}
        />
      </JobListWrapper>
    ) : (
      renderNoDataScreen()
    )

  const onChangeTab = value => {
    const saveTabPayload = {
      jobs_management_filters: {
        tab: value,
      },
    }
    setIsUpdating(true)

    saveManagementUIFilters(saveTabPayload)
  }

  return (
    <ManagementUIAppWrapper
      className="management-ui"
      isNewHeader={featureFlags.APP_HEADER_V2}>
      <div className="management-ui-content">
        <ManagementUITitleContainer align="center">
          <ManagementUITitle>
            {`${formatMessage(messages.managementAppName)} ${formatMessage(
              messages.console,
            )}`}
          </ManagementUITitle>
          <StyledTabs>
            <Tabs
              value={actualTab}
              onChange={(_, value) => {
                if (value !== actualTab) {
                  onChangeTab(value)
                }
              }}>
              <Tab
                value={MANAGEMENT_UI_EVERYTHING}
                label={formatMessage(messages.jobsOverview)}
              />
              {featureFlags.JOB_SCHEDULING_V2 && (
                <Tab
                  value={MANAGEMENT_UI_SCHEDULING}
                  label={formatMessage(messages.scheduling)}
                />
              )}
            </Tabs>
          </StyledTabs>
        </ManagementUITitleContainer>
        <FilterBar
          setIsUpdating={setIsUpdating}
          tmpSites={tmpSites}
          setTmpSites={setTmpSites}
          tmpUsers={tmpUsers}
          setTmpUsers={setTmpUsers}
          tmpUserGroups={tmpUserGroups}
          setTmpUserGroups={setTmpUserGroups}
          tmpCreatedBy={tmpCreatedBy}
          setTmpCreatedBy={setTmpCreatedBy}
          tmpRecurrence={tmpRecurrence}
          setTmpRecurrence={setTmpRecurrence}
          tmpStatus={tmpStatus}
          setTmpStatus={setTmpStatus}
          activeTab={actualTab}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          toggleIsSidebarOpened={toggleIsSidebarOpened}
          fetchManagementUITable={fetchManagementUITable}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
        <CircleLoader loading={isLoading} />
        {renderResults()}
        <PaginationContainer>
          <TablePagination
            count={jobsCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100, 200]}
            onPageChange={(_, nextPage) => {
              setPage(nextPage)
              scrollToTop()
            }}
            onRowsPerPageChange={(_, { props: { value } }) => {
              setPage(0)
              setRowsPerPage(value)
              scrollToTop()
            }}
            component="div"
            labelRowsPerPage={formatMessage({ id: 'rows_per_page' })}
            backIconButtonProps={{
              'aria-label': formatMessage({ id: 'previous_page' }),
            }}
            nextIconButtonProps={{
              'aria-label': formatMessage({ id: 'next_page' }),
            }}
          />
        </PaginationContainer>
      </div>
      <div
        className="job-context-menu-anchor-el"
        style={jobContextMenuPosition}
      />
      <ConfirmDialog
        open={isDialogOpened}
        cancel={{
          color: 'default',
          variant: 'outlined',
          customText: formatMessage(messages.cancel),
        }}
        confirm={{
          color: 'scales-secondary',
          customText: confirmDialogSettings.confirmText,
        }}
        isSubmitDisabled={
          confirmDialogSettings.needsDeletionReason &&
          ((confirmDialogSettings.deletionReasonType ===
            DELETION_REASON_FIELDS.LOOKUP &&
            !deletionReasonFieldValue?.lookup) ||
            (confirmDialogSettings.deletionReasonType ===
              DELETION_REASON_FIELDS.TEXTAREA &&
              !deletionReasonFieldValue?.text) ||
            (confirmDialogSettings.deletionReasonType ===
              DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA &&
              (!deletionReasonFieldValue?.lookup ||
                !deletionReasonFieldValue?.text)))
        }
        onClose={onCloseDeleteJobDialog}
        onConfirmAction={confirmDialogSettings.popupAction(
          deletionReasonFieldValue,
        )}
        className="dialog-primary-custom"
        title={confirmDialogSettings.popupTitle}
        message={
          <>
            {confirmDialogSettings.popupMessage}
            {confirmDialogSettings.needsDeletionReason && (
              <DeletionReason
                onChangeDeletionReasonFieldValue={setDeletionReasonFieldValue}
                deletionReasons={confirmDialogSettings.deletionReasons}
                deletionReasonType={confirmDialogSettings.deletionReasonType}
              />
            )}
          </>
        }
      />
      <PlanNewJob
        isSidebarOpened={isSidebarOpened}
        toggleIsSidebarOpened={toggleIsSidebarOpened}
        fetchManagementUITable={fetchManagementUITable}
      />
    </ManagementUIAppWrapper>
  )
}

ManagementUI.propTypes = {
  intl: REACT_INTL_PROP_TYPE.isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  jobsData: PropTypes.instanceOf(Immutable.List).isRequired,
}

export default connect(
  state => ({
    storePsfs: state.getIn(['managementUI', 'storePsfs']),
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']),
    isLoadingData: state.getIn(['managementUI', 'isLoadingData']),
    isUpdatingData: state.getIn(['managementUI', 'isUpdatingData']),
    jobsData: state.getIn(['managementUI', 'jobsData']),
    jobsManagementFilters: state.getIn([
      'profile',
      'profile',
      'jobsManagementFilters',
    ]),
    jobsCount: state.getIn(['managementUI', 'count']),
  }),
  {
    setSelectedOtherFilters,
    saveManagementUIFilters,
    fetchManagementUIData,
  },
)(injectIntl(ManagementUI))
